// Native date parsing helpers to avoid date-fns locale issues
function safeParseDateString(dateStr: string | undefined | null): Date | null {
  if (!dateStr || typeof dateStr !== 'string' || dateStr.trim() === '') return null;
  try {
    // Parse YYYY-MM-DD as a local calendar date (not UTC midnight) to avoid
    // off-by-one display shifts in negative timezones.
    const dateOnlyMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnlyMatch) {
      const year = parseInt(dateOnlyMatch[1], 10);
      const month = parseInt(dateOnlyMatch[2], 10);
      const day = parseInt(dateOnlyMatch[3], 10);
      const localDate = new Date(year, month - 1, day);
      if (isNaN(localDate.getTime())) return null;
      // Guard against overflow dates like 2026-02-31.
      if (
        localDate.getFullYear() !== year ||
        localDate.getMonth() !== month - 1 ||
        localDate.getDate() !== day
      ) {
        return null;
      }
      return localDate;
    }

    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return null;
    // Additional validation
    const year = date.getFullYear();
    if (isNaN(year) || year < 1900 || year > 2100) return null;
    return date;
  } catch {
    return null;
  }
}

function nativeDifferenceInMinutes(later: Date, earlier: Date): number {
  return Math.round((later.getTime() - earlier.getTime()) / (1000 * 60));
}

// Common airport codes to IANA timezone mapping
const AIRPORT_TIMEZONES: Record<string, string> = {
  // US - East
  'JFK': 'America/New_York', 'LGA': 'America/New_York', 'EWR': 'America/New_York',
  'BOS': 'America/New_York', 'PHL': 'America/New_York', 'DCA': 'America/New_York',
  'IAD': 'America/New_York', 'BWI': 'America/New_York', 'CLT': 'America/New_York',
  'ATL': 'America/New_York', 'MIA': 'America/New_York', 'FLL': 'America/New_York',
  'MCO': 'America/New_York', 'TPA': 'America/New_York', 'PBI': 'America/New_York',
  'DTW': 'America/New_York', 'CLE': 'America/New_York', 'PIT': 'America/New_York',
  // US - Central
  'ORD': 'America/Chicago', 'MDW': 'America/Chicago', 'DFW': 'America/Chicago',
  'IAH': 'America/Chicago', 'HOU': 'America/Chicago', 'AUS': 'America/Chicago',
  'SAT': 'America/Chicago', 'MSP': 'America/Chicago', 'STL': 'America/Chicago',
  'MCI': 'America/Chicago', 'MSY': 'America/Chicago', 'MEM': 'America/Chicago',
  'BNA': 'America/Chicago', 'OKC': 'America/Chicago',
  // US - Mountain
  'DEN': 'America/Denver', 'SLC': 'America/Denver', 'ABQ': 'America/Denver',
  'PHX': 'America/Phoenix', // No DST
  // US - Pacific
  'LAX': 'America/Los_Angeles', 'SFO': 'America/Los_Angeles', 'SJC': 'America/Los_Angeles',
  'OAK': 'America/Los_Angeles', 'SAN': 'America/Los_Angeles', 'SEA': 'America/Los_Angeles',
  'PDX': 'America/Los_Angeles', 'LAS': 'America/Los_Angeles', 'SMF': 'America/Los_Angeles',
  // US - Other
  'HNL': 'Pacific/Honolulu', 'OGG': 'Pacific/Honolulu', 'KOA': 'Pacific/Honolulu',
  'ANC': 'America/Anchorage',
  // Canada
  'YYZ': 'America/Toronto', 'YUL': 'America/Toronto', 'YOW': 'America/Toronto',
  'YVR': 'America/Vancouver', 'YYC': 'America/Edmonton', 'YEG': 'America/Edmonton',
  // Europe
  'LHR': 'Europe/London', 'LGW': 'Europe/London', 'STN': 'Europe/London',
  'MAN': 'Europe/London', 'EDI': 'Europe/London', 'DUB': 'Europe/Dublin',
  'CDG': 'Europe/Paris', 'ORY': 'Europe/Paris',
  'AMS': 'Europe/Amsterdam',
  'FRA': 'Europe/Berlin', 'MUC': 'Europe/Berlin', 'TXL': 'Europe/Berlin', 'BER': 'Europe/Berlin',
  'ZRH': 'Europe/Zurich', 'GVA': 'Europe/Zurich',
  'VIE': 'Europe/Vienna',
  'MAD': 'Europe/Madrid', 'BCN': 'Europe/Madrid',
  'FCO': 'Europe/Rome', 'MXP': 'Europe/Rome',
  'LIS': 'Europe/Lisbon',
  'ATH': 'Europe/Athens',
  'IST': 'Europe/Istanbul',
  'CPH': 'Europe/Copenhagen',
  'ARN': 'Europe/Stockholm', 'OSL': 'Europe/Oslo', 'HEL': 'Europe/Helsinki',
  // Middle East
  'DXB': 'Asia/Dubai', 'AUH': 'Asia/Dubai',
  'DOH': 'Asia/Qatar',
  'TLV': 'Asia/Jerusalem',
  'JED': 'Asia/Riyadh', 'RUH': 'Asia/Riyadh',
  // Asia
  'HKG': 'Asia/Hong_Kong',
  'SIN': 'Asia/Singapore',
  'BKK': 'Asia/Bangkok',
  'KUL': 'Asia/Kuala_Lumpur',
  'NRT': 'Asia/Tokyo', 'HND': 'Asia/Tokyo', 'KIX': 'Asia/Tokyo',
  'ICN': 'Asia/Seoul', 'GMP': 'Asia/Seoul',
  'PVG': 'Asia/Shanghai', 'SHA': 'Asia/Shanghai', 'PEK': 'Asia/Shanghai',
  'TPE': 'Asia/Taipei',
  'MNL': 'Asia/Manila',
  'CGK': 'Asia/Jakarta', 'DPS': 'Asia/Makassar',
  'DEL': 'Asia/Kolkata', 'BOM': 'Asia/Kolkata', 'BLR': 'Asia/Kolkata',
  'CCU': 'Asia/Kolkata', 'MAA': 'Asia/Kolkata', 'HYD': 'Asia/Kolkata',
  // Australia/Pacific
  'SYD': 'Australia/Sydney', 'MEL': 'Australia/Melbourne',
  'BNE': 'Australia/Brisbane', 'PER': 'Australia/Perth',
  'AKL': 'Pacific/Auckland', 'CHC': 'Pacific/Auckland',
  'NAN': 'Pacific/Fiji',
  // South America
  'GRU': 'America/Sao_Paulo', 'GIG': 'America/Sao_Paulo',
  'EZE': 'America/Argentina/Buenos_Aires',
  'SCL': 'America/Santiago', 'LIM': 'America/Lima',
  'BOG': 'America/Bogota',
  // Caribbean/Mexico
  'MEX': 'America/Mexico_City', 'CUN': 'America/Cancun', 'GDL': 'America/Mexico_City',
  'SJU': 'America/Puerto_Rico', 'NAS': 'America/Nassau',
  'MBJ': 'America/Jamaica', 'KIN': 'America/Jamaica',
  // Africa
  'JNB': 'Africa/Johannesburg', 'CPT': 'Africa/Johannesburg',
  'CAI': 'Africa/Cairo', 'CMN': 'Africa/Casablanca',
  'NBO': 'Africa/Nairobi', 'ADD': 'Africa/Addis_Ababa',
  'LOS': 'Africa/Lagos',
};

/**
 * Get IANA timezone for an airport code
 */
export function getAirportTimezone(airportCode: string | undefined | null): string | null {
  if (!airportCode) return null;
  return AIRPORT_TIMEZONES[airportCode.toUpperCase()] || null;
}

/**
 * Format a date/time string with timezone information
 * Handles ISO strings like "2026-04-11T08:30:00" or "2026-04-11T08:30:00+00:00"
 */
export function formatFlightTime(
  dateTimeStr: string | undefined | null,
  timezone?: string | null
): string {
  if (!dateTimeStr) return "—";
  if (typeof dateTimeStr !== 'string' || dateTimeStr.trim() === '') return "—";

  try {
    const date = safeParseDateString(dateTimeStr);
    if (!date) return dateTimeStr;

    // Format: "Apr 11, 8:30 AM" - use manual formatting to avoid date-fns locale issues
    let formatted = manualFormat(date, "MMM d, h:mm a");

    // Add timezone abbreviation if available
    if (timezone) {
      const tzAbbr = getTimezoneAbbreviation(timezone);
      if (tzAbbr) {
        formatted += ` ${tzAbbr}`;
      }
    }

    return formatted;
  } catch (e) {
    console.warn('formatFlightTime error:', e, 'for dateTimeStr:', dateTimeStr);
    return dateTimeStr;
  }
}

/**
 * Format just the time portion with optional timezone
 */
export function formatTimeOnly(
  dateTimeStr: string | undefined | null,
  timezone?: string | null
): string {
  if (!dateTimeStr) return "—";
  if (typeof dateTimeStr !== 'string' || dateTimeStr.trim() === '') return "—";

  try {
    // Handle time-only strings like "17:45"
    if (/^\d{2}:\d{2}(:\d{2})?$/.test(dateTimeStr)) {
      const [hours, minutes] = dateTimeStr.split(':').map(Number);
      if (isNaN(hours) || isNaN(minutes)) return dateTimeStr;
      const period = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours % 12 || 12;
      let formatted = `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;

      if (timezone) {
        const tzAbbr = getTimezoneAbbreviation(timezone);
        if (tzAbbr) formatted += ` ${tzAbbr}`;
      }
      return formatted;
    }

    const date = safeParseDateString(dateTimeStr);
    if (!date) return dateTimeStr;

    // Use manual formatting to avoid date-fns locale issues
    let formatted = manualFormat(date, "h:mm a");

    if (timezone) {
      const tzAbbr = getTimezoneAbbreviation(timezone);
      if (tzAbbr) formatted += ` ${tzAbbr}`;
    }

    return formatted;
  } catch (e) {
    console.warn('formatTimeOnly error:', e, 'for dateTimeStr:', dateTimeStr);
    return dateTimeStr;
  }
}

/**
 * Format a full date with day of week
 */
export function formatFlightDate(dateStr: string | undefined | null): string {
  if (!dateStr) return "—";
  if (typeof dateStr !== 'string' || dateStr.trim() === '') return "—";

  try {
    const date = safeParseDateString(dateStr);
    if (!date) return dateStr;

    // Use manual formatting to avoid date-fns locale issues
    return manualFormat(date, "EEE, MMM d, yyyy");
  } catch (e) {
    console.warn('formatFlightDate error:', e, 'for dateStr:', dateStr);
    return dateStr;
  }
}

/**
 * Get timezone offset in minutes from UTC for a specific date and IANA timezone.
 * Uses Intl API to correctly handle DST.
 * Returns null if timezone is invalid or unsupported.
 */
function getTimezoneOffsetMinutes(timezone: string | null | undefined, date: Date): number | null {
  if (!timezone || typeof timezone !== 'string') return null;
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) return null;

  try {
    // Try using longOffset (Chrome 99+, Safari 15.4+)
    // Falls back to 'short' for older browsers
    let formatter: Intl.DateTimeFormat;
    let useLongOffset = true;

    try {
      formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        timeZoneName: 'longOffset',
      });
    } catch {
      // longOffset not supported, fall back to short
      useLongOffset = false;
      formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        timeZoneName: 'short',
      });
    }

    const parts = formatter.formatToParts(date);
    if (!parts || !Array.isArray(parts)) return null;

    const offsetPart = parts.find(p => p && p.type === 'timeZoneName');
    if (!offsetPart || !offsetPart.value) return null;

    const offsetStr = offsetPart.value;

    if (useLongOffset) {
      // Parse offset like "GMT+08:00" or "GMT-07:00" or "GMT"
      const match = offsetStr.match(/GMT([+-])(\d{2}):(\d{2})/);

      if (!match) {
        // GMT with no offset
        if (offsetStr === 'GMT') return 0;
        return null;
      }

      const sign = match[1] === '+' ? 1 : -1;
      const hours = parseInt(match[2], 10);
      const minutes = parseInt(match[3], 10);

      return sign * (hours * 60 + minutes);
    } else {
      // For short format, we can't get precise offset, return null
      // The duration will be calculated without timezone adjustment
      return null;
    }
  } catch {
    return null;
  }
}

/**
 * Calculate and format flight duration
 * Returns format like "7h 30m" or "12h 10m"
 *
 * When timezone information is provided, it correctly calculates the actual flight
 * duration even when departure and arrival are in different timezones.
 * Properly handles Daylight Saving Time by using Intl API.
 */
export function calculateFlightDuration(
  departureTime: string | undefined | null,
  arrivalTime: string | undefined | null,
  departureTimezone?: string | null,
  arrivalTimezone?: string | null
): string | null {
  if (!departureTime || !arrivalTime) return null;

  try {
    const departure = safeParseDateString(departureTime);
    const arrival = safeParseDateString(arrivalTime);

    if (!departure || !arrival) return null;

    let totalMinutes = nativeDifferenceInMinutes(arrival, departure);

    // If we have timezone information, adjust for timezone differences
    // The ISO times are stored in local time, so we need to convert to the same reference
    // Pass the actual dates to get correct DST-aware offsets
    const depOffset = getTimezoneOffsetMinutes(departureTimezone, departure);
    const arrOffset = getTimezoneOffsetMinutes(arrivalTimezone, arrival);

    if (depOffset !== null && arrOffset !== null) {
      // If arrival timezone is ahead of departure timezone, the actual flight is shorter
      // than what the raw time difference shows.
      // Example: Leave LA at 2pm PDT (-7), arrive NYC at 10pm EDT (-4)
      // Raw diff: 10pm - 2pm = 8 hours
      // Timezone adjustment: EDT is 3 hours ahead of PDT, so actual flight = 8 - 3 = 5 hours
      totalMinutes -= (arrOffset - depOffset);
    }

    // Handle overnight/next-day flights where arrival appears before departure
    // or when crossing the international date line
    if (totalMinutes < 0) {
      totalMinutes += 24 * 60; // Add 24 hours
    }

    // Sanity check for unreasonably long durations (likely data/calculation errors)
    // Longest commercial flights are around 18-19 hours
    if (totalMinutes > 24 * 60) { // More than 24 hours
      // Fall back to simple calculation without timezone adjustment
      totalMinutes = nativeDifferenceInMinutes(arrival, departure);
      if (totalMinutes < 0) {
        totalMinutes += 24 * 60;
      }
    }

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours === 0) {
      return `${minutes}m`;
    } else if (minutes === 0) {
      return `${hours}h`;
    }

    return `${hours}h ${minutes}m`;
  } catch {
    return null;
  }
}

/**
 * Format duration from minutes to a human-readable string.
 * Useful when duration_minutes is already available from API.
 */
export function formatDurationMinutes(minutes: number | undefined | null): string | null {
  if (!minutes || minutes <= 0) return null;

  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (hours === 0) {
    return `${mins}m`;
  } else if (mins === 0) {
    return `${hours}h`;
  }

  return `${hours}h ${mins}m`;
}

/**
 * Extract timezone abbreviation from IANA timezone string
 * e.g., "America/New_York" -> "ET"
 */
export function getTimezoneAbbreviation(timezone: string): string | null {
  if (!timezone) return null;
  
  // Common timezone abbreviations mapping
  const tzMap: Record<string, string> = {
    'America/New_York': 'ET',
    'America/Chicago': 'CT',
    'America/Denver': 'MT',
    'America/Los_Angeles': 'PT',
    'America/Phoenix': 'MST',
    'America/Anchorage': 'AKT',
    'Pacific/Honolulu': 'HST',
    'Europe/London': 'GMT',
    'Europe/Paris': 'CET',
    'Europe/Berlin': 'CET',
    'Europe/Vienna': 'CET',
    'Asia/Tokyo': 'JST',
    'Asia/Shanghai': 'CST',
    'Asia/Hong_Kong': 'HKT',
    'Asia/Singapore': 'SGT',
    'Asia/Dubai': 'GST',
    'Asia/Kolkata': 'IST',
    'Australia/Sydney': 'AEST',
    'Australia/Melbourne': 'AEST',
    'Pacific/Auckland': 'NZST',
  };

  return tzMap[timezone] || timezone.split('/').pop()?.substring(0, 3).toUpperCase() || null;
}

// Month names for manual formatting fallback
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTH_NAMES_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAY_NAMES_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * Manual date formatting that doesn't use date-fns locale lookup.
 * Used as a fallback when date-fns format() fails.
 */
function manualFormat(date: Date, formatStr: string): string {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  const dayOfWeek = date.getDay();
  const hours = date.getHours();
  const minutes = date.getMinutes();

  // Validate all extracted values are valid numbers in expected ranges
  // This prevents accessing arrays with invalid indices (NaN, -1, 12+, etc.)
  if (isNaN(year) || isNaN(month) || isNaN(day) || isNaN(dayOfWeek) || isNaN(hours) || isNaN(minutes)) {
    return "—";
  }
  if (month < 0 || month > 11 || dayOfWeek < 0 || dayOfWeek > 6) {
    return "—";
  }

  // Safe array access with fallbacks
  const monthShort = MONTH_NAMES_SHORT[month] || 'Jan';
  const monthFull = MONTH_NAMES[month] || 'January';
  const dayShort = DAY_NAMES_SHORT[dayOfWeek] || 'Sun';
  const dayFull = DAY_NAMES[dayOfWeek] || 'Sunday';

  // Handle common format patterns manually
  if (formatStr === "MMM d, yyyy" || formatStr === "MMM d, yyyy") {
    return `${monthShort} ${day}, ${year}`;
  }
  if (formatStr === "MMMM d, yyyy") {
    return `${monthFull} ${day}, ${year}`;
  }
  if (formatStr === "EEE, MMM d") {
    return `${dayShort}, ${monthShort} ${day}`;
  }
  if (formatStr === "EEE, MMM d, yyyy") {
    return `${dayShort}, ${monthShort} ${day}, ${year}`;
  }
  if (formatStr === "EEE, dd MMM yyyy") {
    return `${dayShort}, ${day.toString().padStart(2, '0')} ${monthShort} ${year}`;
  }
  if (formatStr === "EEE") {
    return dayShort;
  }
  if (formatStr === "MMM d") {
    return `${monthShort} ${day}`;
  }
  if (formatStr === "MMMM d, yyyy") {
    return `${monthFull} ${day}, ${year}`;
  }
  if (formatStr === "EEEE d MMMM") {
    return `${dayFull} ${day} ${monthFull}`;
  }
  if (formatStr === "h:mm a") {
    const h = hours % 12 || 12;
    const ampm = hours >= 12 ? 'PM' : 'AM';
    return `${h}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  }
  if (formatStr === "MMM d, h:mm a") {
    const h = hours % 12 || 12;
    const ampm = hours >= 12 ? 'PM' : 'AM';
    return `${monthShort} ${day}, ${h}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  }
  if (formatStr === "MMM d, yyyy 'at' h:mm a") {
    const h = hours % 12 || 12;
    const ampm = hours >= 12 ? 'PM' : 'AM';
    return `${monthShort} ${day}, ${year} at ${h}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  }
  if (formatStr === "PPpp") {
    const h = hours % 12 || 12;
    const ampm = hours >= 12 ? 'PM' : 'AM';
    return `${monthShort} ${day}, ${year}, ${h}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  }

  // Default fallback: ISO date
  return `${year}-${(month + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
}

/**
 * Safely format dates with fallback
 * Handles edge cases where parseISO returns a date that isValid() accepts
 * but format() fails due to NaN components
 */
export function safeFormat(
  dateStr: string | undefined | null,
  formatStr: string
): string {
  if (!dateStr) return "—";

  // Additional validation: check if input looks like a valid date string
  if (typeof dateStr !== 'string' || dateStr.trim() === '') return "—";

  try {
    const date = safeParseDateString(dateStr);
    if (!date) return "—";

    // Use manual formatting to avoid date-fns locale issues
    // date-fns format() was causing "xW[t] is undefined" crashes
    return manualFormat(date, formatStr);
  } catch (e) {
    // Log error for debugging but don't crash
    console.warn('safeFormat error:', e, 'for dateStr:', dateStr);
    return "—";
  }
}
